// src/components/BesoinCard.js
import React, { useState } from "react";
import ConfirmDeleteModal from "./ConfirmDeleteModal";
import {
  ChevronDown,
  ChevronUp,
  ThreeDotsVertical,
  PencilSquare,
  Trash,
  StarFill,
  Star,
} from "react-bootstrap-icons";
import { Dropdown, Badge } from "react-bootstrap";
import { useTranslation } from 'react-i18next';
import { STATE_COLORS, getStateKey } from "../constants/besoinConstants";
import { useAuth } from "../services/AuthContext";

const STATUS_COLORS = {
  1: "#8a8a8a", // pas_integre
  2: "#c94f4f", // resistance
  3: "#d9822b", // mal_integre
  4: "#3fa96a", // integre
};

const BesoinCard = ({ besoin, onEdit, onDelete, highlightQuery }) => {
  const { t } = useTranslation();
  const { userGender } = useAuth();
  const [showConfirm, setShowConfirm] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const handleDelete = () => setShowConfirm(true);
  const confirmDelete = () => {
    onDelete(besoin._id);
    setShowConfirm(false);
  };

  const highlightText = (text, query) => {
    if (!query || !text) return text;
    const regex = new RegExp(`(${query})`, "gi");
    return text.split(regex).map((part, idx) =>
      regex.test(part) ? <mark key={idx} style={{ backgroundColor: "#c48f82ff" }}>{part}</mark> : part
    );
  };

  const formattedDate = besoin.createdAt
    ? new Date(besoin.createdAt).toLocaleString('fr-FR', {
        weekday: 'short', year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit'
      })
    : '';

  const statusColor = STATUS_COLORS[besoin.status] || "#8a8a8a";

  return (
    <div
      className="besoin-card"
      style={{
        borderRadius: "6px",
        marginBottom: "12px",
        boxShadow: `0px -3px 1px ${statusColor}`,
        color: "#f5f5f5",
        position: "relative",
      }}
    >
        <div 
          className="besoin-row-grid besoin-card-row"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(prev => !prev);
          }}
          style={{ cursor: "pointer" }}
        >
        {/* États */}
        <div className="besoin-col besoin-col-states">
          {besoin.states && besoin.states.length > 0 ? (
            besoin.states.map((stateId) => (
              <span
                key={stateId}
                className="besoin-state-pill-mini"
                style={{ borderColor: STATE_COLORS[stateId], color: STATE_COLORS[stateId] }}
              >
                {t(getStateKey(stateId, userGender))}
              </span>
            ))
          ) : (
            <span className="besoin-col-empty">—</span>
          )}
        </div>

        {/* Mots-clés */}
        <div className="besoin-col besoin-col-keywords">
          {besoin.keywords && besoin.keywords.length > 0 ? (
            besoin.keywords.map((kw, idx) => (
              <span key={idx} className="besoin-keyword-chip-mini">
                {highlightText(kw, highlightQuery)}
              </span>
            ))
          ) : (
            <span className="besoin-col-empty">—</span>
          )}
        </div>

        {/* Fierté */}
        <div className="besoin-col besoin-col-pride">
          {[1, 2, 3, 4, 5].map((n) =>
            n <= besoin.pride ? (
              <StarFill key={n} size={13} color="#e8c25a" />
            ) : (
              <Star key={n} size={13} color="#666" />
            )
          )}
        </div>

        {/* Fréquence */}
        <div className="besoin-col besoin-col-frequency">
          {besoin.frequency
            ? `${besoin.frequency.count}x / ${t(`besoin.periods_${besoin.frequency.period}`)}`
            : "—"}
        </div>

        {/* Statut */}
        <div className="besoin-col besoin-col-status">
          <Badge
            bg={null}
            style={{
              backgroundColor: statusColor,
              fontWeight: "600",
              fontSize: "0.84em",
              textTransform: "uppercase",
            }}
          >
            {t(`besoin.status_${besoin.status}`)}
          </Badge>
        </div>

        {/* Chevron (dépliage) — même système que NoteCard */}
        <div className="besoin-col besoin-col-chevron">
          <div
            style={{ color: "#f5f5f5", display: "flex", justifyContent: "center" }}
          >
            {expanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </div>
        </div>
      </div>

      {/* --- Détail déplié --- */}
      {expanded && (
        <div
          className="besoin-card-detail"
          style={{ padding: "10px 16px", background: "linear-gradient(145deg, #4A4F5C, #4A4F5C)" }}
        >
          {/* Menu trois points */}
          {/* <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}> */}
          <div style={{ textAlign:"right", marginBottom: "4px", marginRight: "5px" }}>
            <div style={{ fontSize: "0.8em", color: "#aaa" }}>
              {formattedDate}
            </div>

            {onEdit && (
              <Dropdown>
                <Dropdown.Toggle
                  as="button"
                  style={{ background: "none", border: "none", padding: 0, color: "#ccc", cursor: "pointer" }}
                >
                  <ThreeDotsVertical size={18} />
                </Dropdown.Toggle>

                <Dropdown.Menu
                  style={{
                    backgroundColor: "#2B2F38",
                    border: `1px solid #A3A3A3`,
                    borderTop: "0px",
                    borderRadius: "0px 0px 8px 8px",
                    padding: "0",
                    minWidth: "unset",
                    width: "42px",
                    marginTop: "8px",
                  }}
                >
                  <Dropdown.Item
                    onClick={() => onEdit && onEdit(besoin)}
                    style={{ padding: "8px", paddingBottom: "4px", display: "flex", justifyContent: "center", background: "none" }}
                  >
                    <PencilSquare size={12} color="#A3A3A3" />
                  </Dropdown.Item>

                  <Dropdown.Item
                    onClick={handleDelete}
                    style={{ padding: "4px", paddingBottom: "8px", display: "flex", justifyContent: "center", background: "none" }}
                  >
                    <Trash size={24} color="#ff6b6b" />
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            )}
          </div>

          {/* Texte complet du besoin */}
          <p style={{ margin: 0, fontSize: "1em", lineHeight: "1.4", whiteSpace: "pre-line", color: "#ddd", textAlign: "justify" }}>
            {highlightText(besoin.text, highlightQuery)}
          </p>
        </div>
      )}

      <ConfirmDeleteModal show={showConfirm} onClose={() => setShowConfirm(false)} onConfirm={confirmDelete} />
    </div>
  );
};

export default BesoinCard;